import { Patient, PatientType } from "@/src/models/patient";
import { PaginatedPatientFiltersParams } from "@/src/models/patient-filters";
import { PatientMetadata } from "@/src/models/patient-metadata";
import { fetchJSON } from "@/src/requests/utils/fetch-json";

export async function getPatientById(displayId: string): Promise<Patient | null> {
  return fetchJSON<Patient>(`/api/patients/${displayId}`);
}

export async function getPatientsByIds(ids: Set<string>): Promise<Patient[]> {
  return fetchJSON<Patient[]>("/api/patients/batch", {
    method: "POST",
    body: JSON.stringify({ ids: Array.from(ids) }),
    headers: { "Content-Type": "application/json" },
  });
}

export async function getPatientsMetadataByIds(ids: Set<string>): Promise<PatientMetadata[]> {
  if (ids.size === 0) {
    return [];
  }

  return fetchJSON<PatientMetadata[]>("/api/patients/metadata/batch", {
    method: "POST",
    body: JSON.stringify({ ids: Array.from(ids) }),
    headers: { "Content-Type": "application/json" },
  });
}

export async function searchPatientsByQuery(
  query: string,
  options: { limit?: number; type?: PatientType } = {}
): Promise<Patient[]> {
  const trimmed = query.trim();

  if (!trimmed) {
    return [];
  }

  const searchParams = new URLSearchParams({ query: trimmed });

  if (options.limit) {
    searchParams.set("limit", String(options.limit));
  }

  if (options.type) {
    searchParams.set("type", options.type);
  }

  return fetchJSON<Patient[]>(`/api/patients/search?${searchParams.toString()}`);
}

export interface PaginatedPatientsMetadataResponse {
  patients: Record<string, PatientMetadata>;
  totalCount: number;
  hasMore: boolean;
  page: number;
  pageSize: number;
}

export async function getPaginatedPatientsMetadata(
  params: PaginatedPatientFiltersParams = {
    page: 0,
    pageSize: 30,
    sortOrder: "desc",
  }
): Promise<PaginatedPatientsMetadataResponse> {
  const searchParams = new URLSearchParams({
    page: params.page?.toString() ?? "0",
    pageSize: params.pageSize?.toString() ?? "30",
  });

  if (params.status && params.status.length > 0) {
    searchParams.set("status", params.status.join(","));
  }

  if (params.type && params.type.length > 0) {
    searchParams.set("type", params.type.join(","));
  }

  if (params.sortBy) {
    searchParams.set("sortBy", params.sortBy);
  }

  if (params.sortOrder) {
    searchParams.set("sortOrder", params.sortOrder);
  }

  if (params.query) {
    searchParams.set("query", params.query);
  }

  return fetchJSON<PaginatedPatientsMetadataResponse>(`/api/patients/metadata/paginated?${searchParams.toString()}`);
}
